/**
 * /payroll-l4/wage-determinations/$wdId — WD detail page.
 *
 * Header form: WD metadata (number, mod, dates, state, counties, source).
 * Rate editor: per-classification table with inline add/edit/delete and
 * a "Promote drafts" button to bulk-flip draft rows to active.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { api, errorMessage } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { MoneyField } from "@/components/MoneyField";
import type {
  WageDeterminationRate, WageDeterminationWithRates, WdRateStatus,
  WdSource, WdStatus,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/payroll-l4/wage-determinations_/$wdId")({
  component: WdDetailPage,
});

const STATUS_COLOR: Record<WdStatus, "default" | "success" | "warning" | "error"> = {
  active: "success",
  superseded: "warning",
  archived: "default",
};

const RATE_STATUS_COLOR: Record<WdRateStatus, "default" | "success" | "warning" | "error"> = {
  active: "success",
  draft: "warning",
  rejected: "error",
};

const PROJECT_TYPES = ["building", "highway", "heavy", "residential"];
const SOURCES: WdSource[] = ["dol", "sam_gov", "state_dir", "manual"];

function WdDetailPage() {
  const { wdId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: wd, isLoading, error } = useQuery({
    queryKey: ["payroll-l4", "wage-determination", wdId],
    queryFn: async () =>
      (await api(`/api/payroll-l4/wage-determinations/${wdId}`)) as WageDeterminationWithRates,
  });

  // ── Header form state ────────────────────────────────────────────────
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState<Partial<WageDeterminationWithRates>>({});

  useEffect(() => {
    if (wd && !editingHeader) {
      setHeaderForm({
        wd_number: wd.wd_number,
        modification_number: wd.modification_number,
        decision_date: wd.decision_date,
        effective_date: wd.effective_date,
        expiration_date: wd.expiration_date,
        source: wd.source,
        project_type: wd.project_type,
        state_code: wd.state_code,
        counties: wd.counties,
        source_url: wd.source_url,
        notes: wd.notes,
        status: wd.status,
      });
    }
  }, [wd, editingHeader]);

  const saveHeader = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...headerForm };
      if (typeof payload.counties === "string") {
        payload.counties = (payload.counties as string)
          .split(",").map((s) => s.trim()).filter(Boolean);
      }
      return await api(`/api/payroll-l4/wage-determinations/${wdId}`, {
        method: "PATCH", body: payload,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determination", wdId] });
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determinations"] });
      setEditingHeader(false);
    },
  });

  // ── Rate editor state ────────────────────────────────────────────────
  const [editingRate, setEditingRate] = useState<WageDeterminationRate | null>(null);
  const [newRateOpen, setNewRateOpen] = useState(false);

  const promote = useMutation({
    mutationFn: async () =>
      (await api(`/api/payroll-l4/wage-determinations/${wdId}/rates/promote`, {
        method: "POST", body: {},
      })) as { promoted: number },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determination", wdId] });
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determinations"] });
    },
  });

  const deleteRate = useMutation({
    mutationFn: async (rateId: string) =>
      api(`/api/payroll-l4/wage-determinations/${wdId}/rates/${rateId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determination", wdId] });
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determinations"] });
    },
  });

  const deleteWd = useMutation({
    mutationFn: async () =>
      api(`/api/payroll-l4/wage-determinations/${wdId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determinations"] });
      navigate({ to: "/payroll-l4/wage-determinations" });
    },
  });

  if (isLoading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{errorMessage(error)}</Alert></Box>;
  if (!wd) return null;

  const draftCount = wd.rates.filter((r) => r.status === "draft").length;
  const countiesString = Array.isArray(headerForm.counties)
    ? headerForm.counties.join(", ")
    : (headerForm.counties as string | undefined) ?? "";

  return (
    <Box sx={{ p: 3, maxWidth: 1300, mx: "auto" }}>
      {/* ── Top nav ── */}
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton component={Link} to="/payroll-l4/wage-determinations" size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary">Back to WDs</Typography>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h4" fontWeight={700}>
              {wd.wd_number}
              {wd.modification_number ? <span style={{ opacity: 0.55 }}> · Mod {wd.modification_number}</span> : null}
            </Typography>
            <Chip size="small" label={wd.status} color={STATUS_COLOR[wd.status]} />
          </Stack>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {wd.state_code} {wd.counties.length > 0 ? `(${wd.counties.join(", ")})` : ""} · {wd.project_type} · effective {wd.effective_date}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint docPath="payroll-l4/step-2-wd-catalog.md" label="Wage Determinations help" />
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          {!editingHeader && (
            <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setEditingHeader(true)}>
              Edit Header
            </Button>
          )}
          <Button color="error" variant="outlined" onClick={() => {
            if (confirm(`Delete WD ${wd.wd_number} and all ${wd.rate_count} rate rows?`)) deleteWd.mutate();
          }}>
            Delete WD
          </Button>
        </Stack>
      </Stack>

      {/* ── Header card ── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">Header</Typography>
          {editingHeader ? (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
                <TextField label="WD Number" size="small" value={headerForm.wd_number ?? ""}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, wd_number: e.target.value }))} />
                <TextField label="Modification" size="small" value={headerForm.modification_number ?? ""}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, modification_number: e.target.value }))} />
                <TextField label="Decision Date" type="date" size="small" value={headerForm.decision_date ?? ""}
                           InputLabelProps={{ shrink: true }}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, decision_date: e.target.value }))} />
                <TextField label="Effective Date" type="date" size="small" value={headerForm.effective_date ?? ""}
                           InputLabelProps={{ shrink: true }}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, effective_date: e.target.value }))} />
                <TextField label="Expiration Date" type="date" size="small" value={headerForm.expiration_date ?? ""}
                           InputLabelProps={{ shrink: true }}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, expiration_date: e.target.value || null }))} />
                <TextField select label="Source" size="small" value={headerForm.source ?? "dol"}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, source: e.target.value as WdSource }))}>
                  {SOURCES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <TextField select label="Project Type" size="small" value={headerForm.project_type ?? "building"}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, project_type: e.target.value }))}>
                  {PROJECT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField label="State" size="small" value={headerForm.state_code ?? ""}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, state_code: e.target.value.toUpperCase() }))}
                           inputProps={{ maxLength: 2 }} />
                <TextField select label="Status" size="small" value={headerForm.status ?? "active"}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, status: e.target.value as WdStatus }))}>
                  <MenuItem value="active">active</MenuItem>
                  <MenuItem value="superseded">superseded</MenuItem>
                  <MenuItem value="archived">archived</MenuItem>
                </TextField>
                <Box sx={{ gridColumn: "span 2" }}>
                  <TextField fullWidth label="Counties (comma-separated)" size="small" value={countiesString}
                             onChange={(e) => setHeaderForm((f) => ({ ...f, counties: e.target.value as never }))} />
                </Box>
                <TextField fullWidth label="Source URL" size="small" value={headerForm.source_url ?? ""}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, source_url: e.target.value }))}
                           sx={{ gridColumn: "span 2" }} />
                <TextField fullWidth label="Notes" size="small" multiline rows={2} value={headerForm.notes ?? ""}
                           onChange={(e) => setHeaderForm((f) => ({ ...f, notes: e.target.value }))}
                           sx={{ gridColumn: "span 2" }} />
              </Box>
              <Stack direction="row" spacing={1} mt={2}>
                <Button startIcon={<SaveIcon />} variant="contained" onClick={() => saveHeader.mutate()}
                        disabled={saveHeader.isPending}>
                  {saveHeader.isPending ? <CircularProgress size={18} /> : "Save"}
                </Button>
                <Button onClick={() => setEditingHeader(false)}>Cancel</Button>
              </Stack>
              {saveHeader.isError && (
                <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(saveHeader.error)}</Alert>
              )}
            </>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mt: 1 }}>
              <FieldRow label="Decision date" value={wd.decision_date} />
              <FieldRow label="Effective" value={wd.effective_date} />
              <FieldRow label="Expiration" value={wd.expiration_date || "(open-ended)"} />
              <FieldRow label="Source" value={wd.source} />
              <FieldRow label="Project type" value={wd.project_type} />
              <FieldRow label="State" value={wd.state_code} />
              <Box sx={{ gridColumn: "span 3" }}>
                <FieldRow label="Counties" value={wd.counties.length > 0 ? wd.counties.join(", ") : "(none)"} />
              </Box>
              {wd.source_url && (
                <Box sx={{ gridColumn: "span 3" }}>
                  <FieldRow label="Source URL" value={
                    <a href={wd.source_url} target="_blank" rel="noreferrer">{wd.source_url}</a>
                  } />
                </Box>
              )}
              {wd.notes && (
                <Box sx={{ gridColumn: "span 3" }}>
                  <FieldRow label="Notes" value={wd.notes} />
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Rate editor ── */}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">Classification rates</Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{wd.active_rate_count}</strong> active · {draftCount} draft · {wd.rate_count} total
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              {draftCount > 0 && (
                <Button
                  variant="outlined" startIcon={<CheckCircleIcon />}
                  onClick={() => promote.mutate()} disabled={promote.isPending}
                >
                  {promote.isPending ? <CircularProgress size={16} /> : `Promote ${draftCount} draft${draftCount === 1 ? "" : "s"}`}
                </Button>
              )}
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewRateOpen(true)}>
                Add Rate
              </Button>
            </Stack>
          </Stack>

          {promote.isSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Rates are now active. Projects using this wage determination will apply them on the next pay run.
            </Alert>
          )}
          {promote.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(promote.error)}</Alert>
          )}

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Classification</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell align="right">Base $/hr</TableCell>
                  <TableCell align="right">Fringe $/hr</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Zone</TableCell>
                  <TableCell>Effective</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wd.rates.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell><strong>{r.classification}</strong></TableCell>
                    <TableCell><code>{r.classification_code || "—"}</code></TableCell>
                    <TableCell align="right">${Number(r.base_rate).toFixed(2)}</TableCell>
                    <TableCell align="right">${Number(r.fringe_rate).toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <strong>${(Number(r.base_rate) + Number(r.fringe_rate)).toFixed(2)}</strong>
                    </TableCell>
                    <TableCell>{r.zone || "—"}</TableCell>
                    <TableCell>
                      {r.effective_start}
                      {r.effective_end ? ` → ${r.effective_end}` : ""}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} color={RATE_STATUS_COLOR[r.status]} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" onClick={() => setEditingRate(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => {
                          if (confirm(`Delete rate "${r.classification}"?`)) deleteRate.mutate(r.id);
                        }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {wd.rates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No rate rows yet. Click <strong>+ Add Rate</strong>.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>

      {/* ── Add / Edit rate dialog ── */}
      <RateDialog
        open={newRateOpen || editingRate !== null}
        wdId={wdId}
        editing={editingRate}
        defaultEffectiveStart={wd.effective_date}
        onClose={() => { setNewRateOpen(false); setEditingRate(null); }}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determination", wdId] });
          qc.invalidateQueries({ queryKey: ["payroll-l4", "wage-determinations"] });
          setNewRateOpen(false); setEditingRate(null);
        }}
      />
    </Box>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

interface RateDialogProps {
  open: boolean;
  wdId: string;
  editing: WageDeterminationRate | null;
  defaultEffectiveStart: string;
  onClose: () => void;
  onSaved: () => void;
}

function RateDialog({ open, wdId, editing, defaultEffectiveStart, onClose, onSaved }: RateDialogProps) {
  const [form, setForm] = useState({
    classification: "",
    classification_code: "",
    base_rate: "",
    fringe_rate: "0",
    zone: "",
    effective_start: defaultEffectiveStart,
    effective_end: "",
    status: "draft" as WdRateStatus,
    notes: "",
  });

  useEffect(() => {
    if (editing) {
      setForm({
        classification: editing.classification,
        classification_code: editing.classification_code ?? "",
        base_rate: String(editing.base_rate),
        fringe_rate: String(editing.fringe_rate),
        zone: editing.zone ?? "",
        effective_start: editing.effective_start,
        effective_end: editing.effective_end ?? "",
        status: editing.status,
        notes: editing.notes ?? "",
      });
    } else if (open) {
      setForm({
        classification: "",
        classification_code: "",
        base_rate: "",
        fringe_rate: "0",
        zone: "",
        effective_start: defaultEffectiveStart,
        effective_end: "",
        status: "draft",
        notes: "",
      });
    }
  }, [editing, open, defaultEffectiveStart]);

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        classification: form.classification.trim(),
        classification_code: form.classification_code.trim() || null,
        base_rate: form.base_rate,
        fringe_rate: form.fringe_rate || "0",
        zone: form.zone.trim() || null,
        effective_start: form.effective_start,
        effective_end: form.effective_end || null,
        status: form.status,
        notes: form.notes.trim() || null,
      };
      if (editing) {
        return await api(
          `/api/payroll-l4/wage-determinations/${wdId}/rates/${editing.id}`,
          { method: "PATCH", body: payload },
        );
      }
      return await api(
        `/api/payroll-l4/wage-determinations/${wdId}/rates`,
        { method: "POST", body: payload },
      );
    },
    onSuccess: () => onSaved(),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `Edit Rate · ${editing.classification}` : "New Rate"}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
          <TextField required label="Classification" size="small" value={form.classification}
                     onChange={(e) => setForm((f) => ({ ...f, classification: e.target.value }))}
                     placeholder="Carpenter — Group 1" />
          <TextField label="Classification Code" size="small" value={form.classification_code}
                     onChange={(e) => setForm((f) => ({ ...f, classification_code: e.target.value }))}
                     placeholder="CARP_G1" />
          <MoneyField required label="Base Rate / hr" size="small" value={form.base_rate}
                     onChange={(v) => setForm((f) => ({ ...f, base_rate: v }))} />
          <MoneyField label="Fringe Rate / hr" size="small" value={form.fringe_rate}
                     onChange={(v) => setForm((f) => ({ ...f, fringe_rate: v }))} />
          <TextField label="Zone" size="small" value={form.zone}
                     onChange={(e) => setForm((f) => ({ ...f, zone: e.target.value }))}
                     placeholder="Zone 1, City limits, …" />
          <TextField select label="Status" size="small" value={form.status}
                     onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as WdRateStatus }))}>
            <MenuItem value="draft">draft</MenuItem>
            <MenuItem value="active">active</MenuItem>
            <MenuItem value="rejected">rejected</MenuItem>
          </TextField>
          <TextField required label="Effective Start" type="date" size="small" value={form.effective_start}
                     InputLabelProps={{ shrink: true }}
                     onChange={(e) => setForm((f) => ({ ...f, effective_start: e.target.value }))} />
          <TextField label="Effective End" type="date" size="small" value={form.effective_end}
                     InputLabelProps={{ shrink: true }}
                     onChange={(e) => setForm((f) => ({ ...f, effective_end: e.target.value }))}
                     helperText="Leave blank if open-ended" />
          <TextField fullWidth label="Notes" size="small" multiline rows={2} value={form.notes}
                     onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                     sx={{ gridColumn: "span 2" }} />
        </Box>
        {save.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(save.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.classification.trim() || !form.base_rate || save.isPending}
                onClick={() => save.mutate()}>
          {save.isPending ? <CircularProgress size={18} /> : (editing ? "Save" : "Add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
